import { ArrowRight, Brain, Code, Users, Zap, Award, Star, Clock, CheckCircle, ArrowDown, Coffee } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { trackCourseClick } from '../components/GoogleAnalytics';
import '../styles/scrollAnimations.css';

const SeleniumJavaPathPage = () => {
  const courses = [
    {
      id: 1,
      title: "Selenium with Java for Absolute Beginners",
      subtitle: "Foundation Course - Start Here",
      description: "Complete beginner-friendly course on Selenium automation using Java. Learn web automation fundamentals, basic framework concepts, and essential testing practices with comprehensive hands-on examples.",
      longDescription: "Perfect starting point for Selenium automation with Java. Cover automation basics, web element interaction, basic framework concepts, and fundamental testing practices.",
      level: "Beginner",
      duration: "14 hours",
      students: "32,100+",
      rating: 4.6,
      price: "Enroll on Udemy",
      icon: Coffee,
      color: "from-green-500 to-emerald-500",
      borderColor: "border-green-200 hover:border-green-500/50",
      bgColor: "bg-green-50 hover:bg-green-100",
      skills: ["Java Basics", "Selenium WebDriver", "Web Element Interaction", "Test Structure", "Basic Assertions", "IDE Setup"],
      udemy: "cucumber-with-selenium",
      whatYoullLearn: [
        "Selenium automation fundamentals with Java",
        "Web element identification and interaction techniques",
        "Basic test framework setup and management",
        "Essential debugging techniques and best practices",
        "Eclipse/IntelliJ IDE for automation development"
      ]
    },
    {
      id: 2,
      title: "Automation Framework Development with Selenium Java",
      subtitle: "Framework Development",
      description: "Build enterprise-grade Selenium automation frameworks using Java. Master TestNG, Maven, advanced design patterns, data-driven testing, and comprehensive reporting solutions.",
      longDescription: "Master advanced Selenium automation framework development with Java. Cover framework architecture, design patterns, data management, parallel execution, custom reporting, and CI/CD integration.",
      level: "Intermediate",
      duration: "18 hours",
      students: "23,800+",
      rating: 4.8,
      price: "Enroll on Udemy",
      icon: Code,
      color: "from-blue-500 to-cyan-500",
      borderColor: "border-blue-200 hover:border-blue-500/50",
      bgColor: "bg-blue-50 hover:bg-blue-100",
      skills: ["TestNG Framework", "Maven Build Tool", "Page Object Model", "Data-Driven Testing", "ExtentReports", "Jenkins CI/CD"],
      udemy: "automation-framework-with-selenium",
      whatYoullLearn: [
        "Design scalable test automation framework architecture",
        "Implement advanced Java design patterns for testing",
        "Master TestNG features for test organization and execution",
        "Create data-driven tests with Excel and database integration",
        "Generate comprehensive test reports and logs"
      ]
    },
    {
      id: 3,
      title: "Spring Boot for Automation Testing-UI and Microservice",
      subtitle: "Advanced Testing Strategies",
      description: "Advanced techniques for testing modern applications including SPAs, PWAs, and cloud-native applications. Master performance testing, security testing, and advanced automation strategies with Java.",
      longDescription: "Advanced Java testing for modern applications. Cover SPA/PWA testing, performance optimization, security validation, cloud-native testing, and enterprise-grade automation strategies.",
      level: "Advanced",
      duration: "20 hours",
      students: "14,300+",
      rating: 4.9,
      price: "Enroll on Udemy",
      icon: Brain,
      color: "from-purple-500 to-pink-500",
      borderColor: "border-purple-200 hover:border-purple-500/50",
      bgColor: "bg-purple-50 hover:bg-purple-100",
      skills: ["SPA Testing", "PWA Testing", "Performance Testing", "Security Testing", "Cloud Testing", "Modern Apps"],
      udemy: "spring-boot-testing",
      whatYoullLearn: [
        "Test complex Single Page Applications effectively",
        "Implement Progressive Web App testing strategies",
        "Perform automated performance and security testing",
        "Test cloud-native and distributed applications",
        "Handle advanced scenarios and edge cases"
      ]
    },
    {
      id: 4,
      title: "Build+Deploy+Test with Jenkins 2.0",
      subtitle: "Expert Microservice Testing",
      description: "Expert-level course covering event-driven microservice architecture with Java. Learn to build and test distributed systems, handle event sourcing, and implement comprehensive testing strategies for microservices.",
      longDescription: "Expert-level microservice testing with Java. Master event-driven architectures, distributed system testing, service mesh validation, and enterprise microservice strategies.",
      level: "Expert",
      duration: "22 hours",
      students: "8,200+",
      rating: 4.9,
      price: "Enroll on Udemy",
      icon: Zap,
      color: "from-orange-500 to-red-500",
      borderColor: "border-orange-200 hover:border-orange-500/50",
      bgColor: "bg-orange-50 hover:bg-orange-100",
      skills: ["Microservice Testing", "Event Sourcing", "Distributed Testing", "Integration Testing", "System Architecture", "Spring Boot"],
      udemy: "working-with-jenkins",
      whatYoullLearn: [
        "Design and test event-driven microservice systems",
        "Implement comprehensive microservice testing strategies",
        "Handle event sourcing and distributed system testing",
        "Create end-to-end integration test suites",
        "Build resilient and scalable testing frameworks"
      ]
    }
  ];

  // Scroll animation hooks for each course
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const courseRefs = courses.map(() => useScrollAnimation({ threshold: 0.2, delay: 200 }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className={`relative bg-gradient-to-br from-orange-500 via-red-600 to-orange-700 text-white overflow-hidden hero-scroll-fade ${heroVisible ? 'animate' : ''}`}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-orange-900/20 to-red-900/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
                <Coffee className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Selenium with <span className="text-orange-100">Java Mastery</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-orange-50 max-w-4xl mx-auto leading-relaxed mb-6 md:mb-8 px-2">
              Master Selenium automation with Java from beginner to expert. Build enterprise frameworks, test modern applications, 
              and become a Java automation expert.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center items-center">
              <div className="flex items-center gap-2 text-orange-100 text-sm sm:text-base">
                <Star className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400" />
                <span className="font-semibold">78,400+ students already enrolled</span>
              </div>
              <div className="flex items-center gap-2 text-orange-100 text-sm sm:text-base">
                <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>4 comprehensive courses</span>
              </div>
              <div className="flex items-center gap-2 text-orange-100 text-sm sm:text-base">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>74 hours total content</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Path */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Path Header */}
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              Your <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Progressive Journey</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-2">
              Follow this carefully designed path from Java Selenium basics to microservice testing mastery
            </p>
          </div>

          {/* Course Path */}
          <div className="relative">
            {/* Central Path Line - Hidden on mobile */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-0.5 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-500 via-red-500 via-orange-500 to-red-500 opacity-30"></div>

            {courses.map((course, index) => {
              const Icon = course.icon;
              const isEven = index % 2 === 0;
              const { ref, isVisible } = courseRefs[index];
              
              return (
                <div key={course.id} className="relative mb-8 md:mb-16 last:mb-0">
                  {/* Course Card */}
                  <div 
                    ref={ref}
                    className={`section-reveal ${isVisible ? 'animate' : ''} ${isEven ? 'md:pr-8 md:ml-0 md:text-right' : 'md:pl-8 md:ml-auto md:text-left'} md:w-1/2 w-full`}
                  >
                    <div 
                      className={`group relative bg-white rounded-xl md:rounded-2xl p-4 md:p-8 border-2 ${course.borderColor} transition-all duration-500 hover:shadow-2xl transform hover:scale-[1.02] cursor-pointer ${course.bgColor}`}
                      onClick={() => {
                        trackCourseClick(course.title, course.udemy);
                        window.open(`https://www.udemy.com/course/${course.udemy}/`, '_blank');
                      }}
                    >
                      
                      {/* Course Number Badge */}
                      <div className={`absolute ${isEven ? 'md:-right-4' : 'md:-left-4'} -top-3 md:-top-4 right-4 md:right-auto w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r ${course.color} rounded-full flex items-center justify-center text-white font-bold text-base md:text-lg shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                        {index + 1}
                      </div>

                      {/* Course Header */}
                      <div className={`flex items-start gap-3 md:gap-6 mb-4 md:mb-6 ${isEven ? 'md:flex-row-reverse' : ''}`}>
                        <div className={`p-3 md:p-4 rounded-lg md:rounded-xl bg-gradient-to-r ${course.color} shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 md:gap-3 mb-2 flex-wrap">
                            <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2 py-1 rounded-full">
                              {course.level}
                            </span>
                            <div className="flex items-center gap-1 text-gray-500 text-sm">
                              <Clock className="w-3 h-3 md:w-4 md:h-4" />
                              <span>{course.duration}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-500 text-sm">
                              <Users className="w-3 h-3 md:w-4 md:h-4" />
                              <span>{course.students}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-500 text-sm">
                              <Star className="w-3 h-3 md:w-4 md:h-4 text-yellow-500" />
                              <span>{course.rating}</span>
                            </div>
                          </div>
                          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 md:mb-3 leading-tight">
                            {course.title}
                          </h3>
                          <p className="text-xs md:text-sm font-medium text-orange-600 mb-2 md:mb-3 uppercase tracking-wide">
                            {course.subtitle}
                          </p>
                          <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                            {course.description}
                          </p>
                        </div>
                      </div>

                      {/* Skills Tags */}
                      <div className="flex flex-wrap gap-1 md:gap-2 mb-4 md:mb-6">
                        {course.skills.map((skill, skillIndex) => (
                          <span 
                            key={skillIndex}
                            className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full font-medium hover:bg-gray-200 transition-colors duration-200"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>

                      {/* What You'll Learn */}
                      <div className="mb-4 md:mb-6">
                        <h4 className="text-sm md:text-base font-semibold text-gray-900 mb-2 md:mb-3">What you'll learn:</h4>
                        <ul className="space-y-1 md:space-y-2">
                          {course.whatYoullLearn.slice(0, 3).map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start gap-2 text-xs md:text-sm text-gray-600">
                              <CheckCircle className="w-3 h-3 md:w-4 md:h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                          {course.whatYoullLearn.length > 3 && (
                            <li className="text-xs text-orange-600 font-medium ml-5 md:ml-6">
                              +{course.whatYoullLearn.length - 3} more learning objectives
                            </li>
                          )}
                        </ul>
                      </div>

                      {/* Action Button */}
                      <div className="flex items-center gap-3 pt-3 md:pt-4 border-t border-gray-100">
                        <button className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl font-medium text-sm md:text-base hover:from-orange-700 hover:to-red-700 transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2 group-hover:shadow-lg">
                          {course.price}
                          <ArrowRight className="w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform duration-200" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Path Connector */}
                  {index < courses.length - 1 && (
                    <div className="flex justify-center mt-6 md:mt-8 mb-2 md:mb-4">
                      <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-r from-gray-200 to-gray-300 shadow-sm">
                        <ArrowDown className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-orange-50 to-red-100">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
            Prefer Learning in a Different Language?
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-6 md:mb-8 max-w-2xl mx-auto">
            If you want to learn Selenium automation with C# .NET instead of Java, check out our comprehensive C# learning path.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
            <Link
              to="/paths/selenium-csharp"
              className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-base md:text-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Start Learning Selenium in C# .NET
            </Link>
            <Link 
              to="/automation"
              className="border-2 border-gray-300 text-gray-700 px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-base md:text-lg hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 transform hover:scale-105"
            >
              Browse All Courses
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeleniumJavaPathPage;